/**
 * Utilitas Generator QRIS Dinamis (EMVCo / Bank Indonesia / ASPI Specification)
 * 
 * Mengonversi QRIS Statis toko menjadi QRIS Dinamis dengan nominal transaksi
 * otomatis terisi & terkunci pada aplikasi m-banking dan e-wallet pembeli.
 */

// Sample QRIS valid standar Bank Indonesia sebagai default/demo
export const DEFAULT_DEMO_QRIS =
  '00020101021126580014ID.LINKAJA.WWW01189360091100220935570208102209350303UMI51440014ID.DANA.WWW01189360091530220935570208102209350303UMI5204599953033605802ID5918TOKO EMAS KOMALA DEWI6007DENPASAR61058011262070703A0163047D5B';

/**
 * Menghitung checksum CRC16-CCITT (Polinomial 0x1021, Initial 0xFFFF)
 * Standar yang digunakan dalam spesifikasi EMVCo MPM dan QRIS Nasional.
 * 
 * @param {string} str - String payload QRIS yang akan dihitung (termasuk tag '6304')
 * @returns {string} 4 karakter heksadesimal uppercase
 */
export function crc16(str) {
  let crc = 0xffff;
  for (let i = 0; i < str.length; i++) {
    crc ^= str.charCodeAt(i) << 8;
    for (let j = 0; j < 8; j++) {
      if ((crc & 0x8000) !== 0) {
        crc = ((crc << 1) ^ 0x1021) & 0xffff;
      } else {
        crc = (crc << 1) & 0xffff;
      }
    }
  }
  return crc.toString(16).toUpperCase().padStart(4, '0');
}

/**
 * Memvalidasi apakah string merupakan QRIS yang valid
 * 
 * @param {string} qrisStr 
 * @returns {boolean}
 */
export function validateQRIS(qrisStr) {
  if (!qrisStr || typeof qrisStr !== 'string') return false;
  const clean = qrisStr.trim();
  // Minimal harus diawali format indicator 000201 dan memiliki panjang memadai
  return clean.startsWith('000201') && clean.length >= 50;
}

/**
 * Mengonversi QRIS Statis menjadi QRIS Dinamis dengan nominal transaksi tertentu.
 * 
 * @param {string} rawStaticQris - String QRIS statis dari toko (atau string QRIS awal)
 * @param {number|string} amount - Nominal transaksi dalam Rupiah (misal: 1500000)
 * @returns {string} String QRIS Dinamis lengkap dengan checksum CRC16 yang valid
 */
export function generateDynamicQRIS(rawStaticQris, amount) {
  const baseSource = (rawStaticQris && typeof rawStaticQris === 'string' && rawStaticQris.trim().length > 20)
    ? rawStaticQris.trim()
    : DEFAULT_DEMO_QRIS;

  let qris = baseSource;

  // 1. Buang checksum CRC lama (Tag 6304 + 4 hex characters) dari ujung string
  const crcMatch = qris.match(/6304[A-Fa-f0-9]{4}$/);
  if (crcMatch) {
    qris = qris.substring(0, crcMatch.index);
  } else if (qris.endsWith('6304')) {
    qris = qris.substring(0, qris.length - 4);
  }

  // 2. Ubah Tag 01 (Point of Initiation Method) dari Statis (11) menjadi Dinamis (12)
  if (qris.startsWith('000201010211')) {
    qris = qris.replace('000201010211', '000201010212');
  } else if (qris.includes('010211')) {
    qris = qris.replace('010211', '010212');
  }

  // 3. Susun Tag 54 (Transaction Amount)
  const numAmount = Math.round(Number(amount) || 0);
  const amtStr = numAmount > 0 ? numAmount.toString() : '';
  const tag54 = amtStr ? `54${String(amtStr.length).padStart(2, '0')}${amtStr}` : '';

  // 4. Injeksi Tag 54 sebelum Tag 58 (Country Code '5802ID')
  const currencyMatch = '5303360';
  const countryMatch = '5802ID';

  const idxCountry = qris.indexOf(countryMatch);
  const idxCurrency = qris.indexOf(currencyMatch);

  if (idxCountry !== -1) {
    let beforeCountry = qris.substring(0, idxCountry);
    const afterCountry = qris.substring(idxCountry);

    // Jika sudah pernah ada Tag 54 antara mata uang dan kode negara, bersihkan terlebih dahulu
    if (idxCurrency !== -1 && idxCurrency < idxCountry) {
      beforeCountry = qris.substring(0, idxCurrency + currencyMatch.length);
    }

    qris = beforeCountry + tag54 + afterCountry;
  } else {
    // Fallback bila struktur berbeda: letakkan sebelum 6304
    qris = qris + tag54;
  }

  // 5. Tambahkan tag 6304 dan kalkulasi ulang nilai CRC16-CCITT
  const toSign = qris + '6304';
  const checksum = crc16(toSign);

  return toSign + checksum;
}
